import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { format } from "date-fns";
import axios from "axios";

interface LiveSession {
  _id: string;
  courseId: {
    _id: string;
    title: string;
    thumbnail?: string;
  };
  hostUserId: {
    _id: string;
    name: string;
    email: string;
  };
  startAt: string;
  endAt: string;
  timezone: string;
  meetLink?: string;
  status: "scheduled" | "cancelled" | "completed";
}

const LiveSessions: React.FC = () => {
  const { user } = useAuth();
  const [filter, setFilter] = useState<"scheduled" | "all">("scheduled");

  const { data: liveSessions = [], isLoading } = useQuery({
    queryKey: ["live-sessions", filter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filter === "scheduled") {
        params.append("status", "scheduled");
        params.append("upcoming", "true");
      }

      const response = await axios.get(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/api/elearning/live-sessions?${params}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data.data;
    },
  });

  const handleJoinSession = (meetLink: string) => {
    if (meetLink) {
      window.open(meetLink, "_blank");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return "bg-green-100 text-green-800";
      case "live":
        return "bg-red-100 text-red-800";
      case "ended":
        return "bg-gray-100 text-gray-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Function to determine session status based on time
  const getSessionStatus = (session: any) => {
    const now = new Date();
    const startTime = new Date(session.startAt);
    const endTime = new Date(session.endAt);

    if (session.status === "cancelled") return "cancelled";
    if (session.status === "completed") return "completed";
    if (now > endTime) return "ended";
    if (now >= startTime && now <= endTime) return "live";
    return "scheduled";
  };

  if (isLoading && liveSessions.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Live Sessions</h1>
        <p className="text-gray-600">
          Join live classes and interactive sessions with your instructors.
        </p>
      </div>

      {/* Filter */}
      <div className="mb-6">
        <div className="flex gap-4">
          <button
            onClick={() => setFilter("scheduled")}
            className={`px-4 py-2 rounded-lg font-medium ${
              filter === "scheduled"
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            Upcoming Sessions
          </button>
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-lg font-medium ${
              filter === "all"
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            All Sessions
          </button>
        </div>
      </div>

      {/* Sessions List */}
      {liveSessions.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No live sessions found
          </h3>
          <p className="text-gray-500">
            {filter === "scheduled"
              ? "You don't have any upcoming live sessions."
              : "You haven't participated in any live sessions yet."}
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {liveSessions.map((session: any) => (
            <div
              key={session._id}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
            >
              {/* Course Thumbnail */}
              <div className="h-32 bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                {session.courseId.thumbnail ? (
                  <img
                    src={session.courseId.thumbnail}
                    alt={session.courseId.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-white text-center">
                    <svg
                      className="mx-auto h-8 w-8 mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-sm font-medium">Live Session</p>
                  </div>
                )}
              </div>

              {/* Session Details */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                    {session.courseId.title}
                  </h3>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                      getSessionStatus(session)
                    )}`}
                  >
                    {getSessionStatus(session)}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    {session.hostUserId.name}
                  </div>

                  <div className="flex items-center text-sm text-gray-600">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {format(new Date(session.startAt), "PPP 'at' p")}
                  </div>

                  <div className="flex items-center text-sm text-gray-600">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    {Math.round(
                      (new Date(session.endAt).getTime() -
                        new Date(session.startAt).getTime()) /
                        (1000 * 60)
                    )}{" "}
                    minutes
                  </div>
                </div>

                {/* Join Button */}
                {getSessionStatus(session) === "scheduled" &&
                  session.meetLink && (
                    <button
                      onClick={() => handleJoinSession(session.meetLink!)}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                        />
                      </svg>
                      Join Session
                    </button>
                  )}

                {getSessionStatus(session) === "live" && session.meetLink && (
                  <button
                    onClick={() => handleJoinSession(session.meetLink!)}
                    className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                      />
                    </svg>
                    Join Live Session
                  </button>
                )}

                {(getSessionStatus(session) === "ended" ||
                  getSessionStatus(session) === "completed") && (
                  <div className="text-center text-sm text-gray-500 py-2">
                    Session {getSessionStatus(session)}
                  </div>
                )}

                {getSessionStatus(session) === "cancelled" && (
                  <div className="text-center text-sm text-red-500 py-2">
                    Session cancelled
                  </div>
                )}

                {session.status === "cancelled" && (
                  <div className="text-center text-sm text-red-500 py-2">
                    Session cancelled
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveSessions;
